// Row 4.4 — the shell's permission-denied surface per the D-UAM-04 refined matrix
// (UAM spec doc 15 §8). This batch ships the COMPONENT + tests; no live deny path renders it
// yet (route/beforeLoad wiring = master-spec row 3.11, out of this batch).
//
// Reason variants map to the D-UAM-04 matrix rows this component serves:
//   • role    — direct-URL deny for a role gap → 403 + "request access" CTA.
//   • zone    — direct-URL deny for a zone/workspace boundary → 403 + CTA.
//   • overlay — compliance-overlay deny → 403 + specific remediation message/link.
//   • flag    — feature-flag-off deep link → generic "not available", NO permission
//               language, NO CTA (matrix surface 5 semantics).
//   • sealed  — WALL RULE (doc 15 §8 403-design + 48.24 sealed existence): the resource's
//               existence must not be disclosed. Renders a generic unavailable panel with
//               NO resource name and NO request-access CTA — a "request access to X"
//               affordance would itself confirm X exists.
import { Lock } from "lucide-react";
import { ChromeForbidden } from "@perlab/ui";
import { EmptyState } from "./EmptyState";

export type ForbiddenReason = "role" | "zone" | "overlay" | "flag" | "sealed";

export interface ShellForbiddenProps {
  reason: ForbiddenReason;
  /** Human label of what was requested (shown for role/zone/overlay only — NEVER for sealed). */
  resourceLabel?: string;
  /** Overlay remediation message (e.g. training link text), per matrix surface 6. */
  remediation?: string;
  /** Request-access CTA (role/zone/overlay). Omitted → no CTA rendered. */
  onRequestAccess?: () => void;
}

export function ShellForbidden({
  reason,
  resourceLabel,
  remediation,
  onRequestAccess,
}: ShellForbiddenProps): React.ReactElement {
  // Sealed + flag surfaces: generic, no permission language, no CTA, no resource identity.
  if (reason === "sealed") {
    return (
      <div data-app="shell-forbidden" data-reason="sealed">
        <EmptyState
          icon={<Lock size={28} aria-hidden />}
          title="This page isn't available"
          description="It may not exist, or it may not be available to you."
        />
      </div>
    );
  }
  if (reason === "flag") {
    return (
      <div data-app="shell-forbidden" data-reason="flag">
        <EmptyState
          title="This feature isn't available"
          description="It isn't enabled in this workspace right now."
        />
      </div>
    );
  }

  const description =
    reason === "overlay"
      ? remediation ?? "This content is restricted by a compliance requirement."
      : reason === "zone"
        ? "You're outside the workspace or engagement this content belongs to. Switch context, or ask for access."
        : "You need an additional role on this engagement to view this.";

  return (
    // No role="alert" here — ChromeForbidden's root already renders the assertive live region;
    // nesting a second one double-announces to screen readers.
    <div data-app="shell-forbidden" data-reason={reason}>
      <ChromeForbidden
        appName={resourceLabel ?? "this content"}
        description={description}
        onRequestAccess={onRequestAccess}
      />
    </div>
  );
}
